import type { Id } from "@convex/_generated/dataModel";
import { stripPhoneNumberMask } from "@plasma/utils/phone-numbers";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { employeesCreate } from "~/api-refs";
import type {
  Step1Data,
  Step3Data,
} from "~/components/employee-form-modal/common";
import { EmployeeForm } from "~/components/employee-form-modal/employee-form";
import { Dialog, DialogContent } from "~/components/ui/dialog";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { ProductEvents } from "~/lib/posthog";

// V4.1: Route-based modal for URL context tracking
// Search params include optional newRoleId for inline role creation callback
const searchParamsSchema = z.object({
  newRoleId: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/employees/new")({
  component: NewEmployeeModal,
  validateSearch: searchParamsSchema,
});

function NewEmployeeModal() {
  const navigate = useNavigate();
  const { newRoleId } = Route.useSearch();

  const [currentStep, setCurrentStep] = useState(0);
  const [step1Data, setStep1Data] = useState<Step1Data | undefined>();
  const [step2Data, setStep2Data] = useState<{ pin: string } | undefined>();
  const [step3Data, setStep3Data] = useState<Step3Data | undefined>();

  // V4.1: Handle newly created role from child route
  useEffect(() => {
    if (newRoleId) {
      setStep3Data((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          roles: [
            ...prev.roles,
            toConvexTableId("organizationRoles", newRoleId),
          ],
        };
      });
      // Clear the search param after processing
      void navigate({ to: "/employees/new", search: {}, replace: true });
    }
  }, [newRoleId, navigate]);

  const handleClose = () => {
    void navigate({ to: "/employees" });
  };

  // V4.1: Navigation callback for inline role creation
  const handleOpenCreateRole = (roleName: string) => {
    void navigate({
      to: "/employees/new/create-role",
      search: { roleName },
    });
  };

  const createEmployeeMutation = useMutation({
    mutationFn: (data: {
      imageUrl?: string;
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber?: string;
      hourlyRate?: number;
      pin: string;
      locations: Id<"locations">[];
      roles: Id<"organizationRoles">[];
      addToFutureLocations: boolean;
    }) => convexClient.mutation(employeesCreate, data),
    onSuccess: (result) => {
      // Track employee creation in PostHog
      if (result) {
        ProductEvents.employeeCreated(result);
      }
    },
  });

  const handleStep3Submit = async (step3data: Step3Data) => {
    const employeeEmail = step1Data?.email?.trim();

    await createEmployeeMutation.mutateAsync({
      imageUrl: step1Data?.imageUrl,
      firstName: step1Data?.firstName ?? "",
      lastName: step1Data?.lastName ?? "",
      email: employeeEmail ?? "",
      phoneNumber: step1Data?.phoneNumber
        ? stripPhoneNumberMask(step1Data?.phoneNumber)
        : undefined,
      hourlyRate: step1Data?.hourlyRate
        ? Number.parseFloat(String(step1Data?.hourlyRate))
        : undefined,
      pin: step2Data?.pin ?? "",
      locations: step3data.locations.map((id) =>
        toConvexTableId("locations", id)
      ),
      roles: step3data.roles.map((id) =>
        toConvexTableId("organizationRoles", id)
      ),
      addToFutureLocations: step3data.addToFutureLocations,
    });

    // Workspace invitations are handled by the vortex-auth org member surface
    // (Settings > Organization Members), not at employee-creation time. The
    // employee record is created here; direct the admin to send the invite if
    // they supplied an email.
    if (employeeEmail) {
      toast.success(
        "Employee created. Invite them to the workspace from Settings > Organization Members."
      );
    } else {
      toast.success("Employee created successfully");
    }

    handleClose();
  };

  return (
    <>
      <Dialog
        onOpenChange={(open) => {
          if (!open) {
            handleClose();
          }
        }}
        open
      >
        <DialogContent noCloseButton>
          <EmployeeForm
            currentStep={currentStep}
            onOpenCreateRoleModal={handleOpenCreateRole}
            onStep1Submit={(step1data: Step1Data) => {
              setStep1Data(step1data);
              setCurrentStep(1);
            }}
            onStep2Submit={(step2data: { pin: string }) => {
              setStep2Data(step2data);
              setCurrentStep(2);
            }}
            onStep3Save={(step3data: Step3Data) => setStep3Data(step3data)}
            onStep3Submit={handleStep3Submit}
            setCurrentStep={setCurrentStep}
            step1Data={step1Data}
            step2Data={step2Data}
            step3Data={step3Data}
          />
        </DialogContent>
      </Dialog>
      {/* V4.1: Outlet for child routes (create-role modal) */}
      <Outlet />
    </>
  );
}
